/**
 * Variant Integration Service
 * 
 * Bridges the merchant product modal with the variant management system,
 * enabling SKU generation for variant combinations and maintaining
 * parent-child product relationships.
 */

import { skuGenerationService, SKUGenerationOptions } from './skuGenerationService'
import { MerchantProduct } from 'src/shared/components/MerchantProductModal/types'

// Import variant types from the existing VariantOptionsBuilder
export interface VariantOption {
  id: string
  name: string
  type: string
  values: VariantOptionValue[]
  required: boolean
  sortOrder: number
}

export interface VariantOptionValue {
  id: string
  value: string
  displayName: string
  colorCode?: string
  imageUrl?: string
  metadata?: Record<string, any>
}

export interface VariantCombination {
  id: string
  sku: string
  options: Record<string, string> // optionId -> valueId mapping
  optionValues: Record<string, VariantOptionValue> // optionId -> value object
  price?: number
  compareAtPrice?: number
  stock?: number
  weight?: number
  status: 'enabled' | 'disabled' | 'out_of_stock'
  linkedMerchantProduct?: MerchantProduct | null
  parentProductId?: string
}

export interface ProductVariantRelationship {
  parentProductId: string
  parentSku: string
  variants: VariantCombination[]
  linkedMerchantProducts: Map<string, MerchantProduct> // variantId -> MerchantProduct
  skuPattern: string
  baseSkuOptions: SKUGenerationOptions
}

export interface VariantLinkingResult {
  success: boolean
  linkedVariants: string[]
  failedVariants: string[]
  generatedSkus: Record<string, string> // variantId -> SKU
  errors: string[]
}

export interface VariantSKUGenerationOptions {
  baseSkuOptions: SKUGenerationOptions
  pattern: string
  includeVariantCode: boolean
  variantCodeFormat: 'short' | 'full' | 'custom'
  customVariantPattern?: string
}

class VariantIntegrationService {
  private static instance: VariantIntegrationService
  private variantRelationships: Map<string, ProductVariantRelationship> = new Map()

  static getInstance(): VariantIntegrationService {
    if (!this.instance) {
      this.instance = new VariantIntegrationService()
    }
    return this.instance
  }

  /**
   * Link merchant products to variant combinations
   */
  public linkMerchantProductsToVariants(
    parentProductId: string,
    variantCombinations: VariantCombination[],
    merchantProducts: MerchantProduct[],
    linkingStrategy: 'auto' | 'manual' = 'auto'
  ): VariantLinkingResult {
    const result: VariantLinkingResult = {
      success: false,
      linkedVariants: [],
      failedVariants: [],
      generatedSkus: {},
      errors: []
    }

    try {
      if (linkingStrategy === 'auto') {
        return this.autoLinkVariantsToMerchantProducts(
          parentProductId,
          variantCombinations,
          merchantProducts
        )
      } else {
        return this.manualLinkVariantsToMerchantProducts(
          parentProductId,
          variantCombinations,
          merchantProducts
        )
      }
    } catch (error) {
      result.errors.push(error instanceof Error ? error.message : 'Unknown error')
      return result
    }
  }

  /**
   * Generate SKUs for variant combinations based on linked merchant products
   */
  public generateVariantSKUs(
    parentProductId: string,
    variantCombinations: VariantCombination[],
    skuOptions: VariantSKUGenerationOptions
  ): Record<string, string> {
    const generatedSKUs: Record<string, string> = {}

    for (const variant of variantCombinations) {
      try {
        const variantCode = this.generateVariantCode(variant, skuOptions.variantCodeFormat)
        
        const skuGenerationOptions: SKUGenerationOptions = {
          ...skuOptions.baseSkuOptions,
          variant: variantCode,
          sequence: this.getVariantSequence(parentProductId, variant.id)
        }

        if (variant.linkedMerchantProduct) {
          // Use merchant product data for SKU generation
          skuGenerationOptions.brand = variant.linkedMerchantProduct.brand
          skuGenerationOptions.category = variant.linkedMerchantProduct.category?.[0]?.id || ''
          skuGenerationOptions.supplier = variant.linkedMerchantProduct.supplier
          skuGenerationOptions.product = variant.linkedMerchantProduct.name
        }

        const skuResult = skuGenerationService.generateSKU(skuGenerationOptions)
        
        if (skuResult.isUnique) {
          generatedSKUs[variant.id] = skuResult.sku
        } else {
          // Try with incremented sequence
          const fallbackResult = skuGenerationService.generateSKU({
            ...skuGenerationOptions,
            sequence: skuGenerationOptions.sequence! + 1
          })
          generatedSKUs[variant.id] = fallbackResult.sku
        }
      } catch (error) {
        console.error(`Failed to generate SKU for variant ${variant.id}:`, error)
        generatedSKUs[variant.id] = `VAR-${variant.id}-ERROR`
      }
    }

    // Update stored relationships
    this.updateVariantRelationship(parentProductId, {
      parentProductId,
      parentSku: skuOptions.baseSkuOptions.prefix || 'PARENT',
      variants: variantCombinations,
      linkedMerchantProducts: this.extractMerchantProductMap(variantCombinations),
      skuPattern: skuOptions.pattern,
      baseSkuOptions: skuOptions.baseSkuOptions
    })

    return generatedSKUs
  }

  /**
   * Create parent-child product relationships for marketplace sync
   */
  public establishProductHierarchy(
    parentProduct: any, // Parent product data
    variantCombinations: VariantCombination[],
    linkedMerchantProducts: Map<string, MerchantProduct>
  ): Record<string, any> {
    const hierarchy = {
      parent: {
        ...parentProduct,
        hasVariants: true,
        variantCount: variantCombinations.length,
        linkedMerchantProductCount: linkedMerchantProducts.size
      },
      children: variantCombinations.map(variant => ({
        variantId: variant.id,
        sku: variant.sku,
        parentSku: parentProduct.sku,
        options: variant.optionValues,
        linkedMerchantProduct: variant.linkedMerchantProduct,
        inheritedData: this.getInheritedDataFromParent(parentProduct, variant),
        marketplaceSpecificData: this.generateMarketplaceData(variant)
      }))
    }

    return hierarchy
  }

  /**
   * Sync variant inventory with linked merchant products
   */
  public syncVariantInventory(
    parentProductId: string,
    inventoryUpdates: Record<string, { stock: number; price?: number }>
  ): boolean {
    const relationship = this.variantRelationships.get(parentProductId)
    if (!relationship) return false

    try {
      for (const [variantId, update] of Object.entries(inventoryUpdates)) {
        const variant = relationship.variants.find(v => v.id === variantId)
        if (variant) {
          variant.stock = update.stock
          if (update.price !== undefined) {
            variant.price = update.price
          }
        }
      }

      // Update the stored relationship
      this.variantRelationships.set(parentProductId, relationship)
      return true
    } catch (error) {
      console.error('Failed to sync variant inventory:', error)
      return false
    }
  }

  /**
   * Get variant relationship data
   */
  public getVariantRelationship(parentProductId: string): ProductVariantRelationship | null {
    return this.variantRelationships.get(parentProductId) || null
  }

  /**
   * Unlink merchant product from variant
   */
  public unlinkMerchantProductFromVariant(
    parentProductId: string,
    variantId: string
  ): boolean {
    const relationship = this.variantRelationships.get(parentProductId)
    if (!relationship) return false

    const variant = relationship.variants.find(v => v.id === variantId)
    if (variant) {
      variant.linkedMerchantProduct = null
      relationship.linkedMerchantProducts.delete(variantId)
      this.variantRelationships.set(parentProductId, relationship)
      return true
    }

    return false
  }

  // Private methods

  private autoLinkVariantsToMerchantProducts(
    parentProductId: string,
    variantCombinations: VariantCombination[],
    merchantProducts: MerchantProduct[]
  ): VariantLinkingResult {
    const result: VariantLinkingResult = {
      success: false,
      linkedVariants: [],
      failedVariants: [],
      generatedSkus: {},
      errors: []
    }

    // Auto-linking strategy: match by similarity in names, categories, brands
    for (const variant of variantCombinations) {
      const variantName = this.getVariantDisplayName(variant)
      
      // Find best matching merchant product
      const matchedProduct = this.findBestMerchantProductMatch(variantName, merchantProducts)
      
      if (matchedProduct) {
        variant.linkedMerchantProduct = matchedProduct
        result.linkedVariants.push(variant.id)
        
        // Generate SKU for linked variant
        const sku = this.generateSKUForLinkedVariant(variant, matchedProduct)
        result.generatedSkus[variant.id] = sku
        variant.sku = sku
      } else {
        result.failedVariants.push(variant.id)
      }
    }

    result.success = result.linkedVariants.length > 0
    return result
  }

  private manualLinkVariantsToMerchantProducts(
    parentProductId: string,
    variantCombinations: VariantCombination[],
    merchantProducts: MerchantProduct[]
  ): VariantLinkingResult {
    // Manual linking requires external selection logic
    // This method provides structure for manual linking implementation
    return {
      success: false,
      linkedVariants: [],
      failedVariants: variantCombinations.map(v => v.id),
      generatedSkus: {},
      errors: ['Manual linking requires user interaction']
    }
  }

  private generateVariantCode(
    variant: VariantCombination,
    format: 'short' | 'full' | 'custom'
  ): string {
    const values = Object.values(variant.optionValues)
    
    switch (format) {
      case 'short':
        return values
          .map(v => v.value.slice(0, 3).toUpperCase())
          .join('')
          .slice(0, 6)
      
      case 'full':
        return values
          .map(v => v.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
          .join('-')
      
      case 'custom':
        // Custom format implementation would go here
        return this.generateVariantCode(variant, 'short')
      
      default:
        return this.generateVariantCode(variant, 'short')
    }
  }

  private getVariantSequence(parentProductId: string, variantId: string): number {
    const relationship = this.variantRelationships.get(parentProductId)
    if (!relationship) return 1

    const variantIndex = relationship.variants.findIndex(v => v.id === variantId)
    return variantIndex + 1
  }

  private extractMerchantProductMap(
    variantCombinations: VariantCombination[]
  ): Map<string, MerchantProduct> {
    const map = new Map<string, MerchantProduct>()
    
    for (const variant of variantCombinations) {
      if (variant.linkedMerchantProduct) {
        map.set(variant.id, variant.linkedMerchantProduct)
      }
    }
    
    return map
  }

  private updateVariantRelationship(
    parentProductId: string,
    relationship: ProductVariantRelationship
  ): void {
    this.variantRelationships.set(parentProductId, relationship)
  }

  private getVariantDisplayName(variant: VariantCombination): string {
    const optionValues = Object.values(variant.optionValues)
    return optionValues.map(v => v.displayName).join(' ')
  }

  private findBestMerchantProductMatch(
    variantName: string,
    merchantProducts: MerchantProduct[]
  ): MerchantProduct | null {
    // Simple matching by name similarity
    const normalizedVariantName = variantName.toLowerCase()
    
    for (const product of merchantProducts) {
      const normalizedProductName = product.name.toLowerCase()
      
      // Check for partial matches
      if (normalizedProductName.includes(normalizedVariantName) ||
          normalizedVariantName.includes(normalizedProductName)) {
        return product
      }
    }
    
    return null
  }

  private generateSKUForLinkedVariant(
    variant: VariantCombination,
    merchantProduct: MerchantProduct
  ): string {
    const variantCode = this.generateVariantCode(variant, 'short')
    
    const skuResult = skuGenerationService.generateSKU({
      pattern: 'brand-product-variant',
      brand: merchantProduct.brand,
      product: merchantProduct.name,
      variant: variantCode
    })
    
    return skuResult.sku
  }

  private getInheritedDataFromParent(parentProduct: any, variant: VariantCombination): any {
    return {
      category: parentProduct.category,
      brand: parentProduct.brand,
      description: parentProduct.description,
      tags: parentProduct.tags,
      weight: variant.weight || parentProduct.weight,
      dimensions: parentProduct.dimensions
    }
  }

  private generateMarketplaceData(variant: VariantCombination): any {
    return {
      title: this.getVariantDisplayName(variant),
      attributes: variant.optionValues,
      status: variant.status,
      inventory: {
        quantity: variant.stock || 0,
        trackInventory: true,
        allowBackorder: false
      }
    }
  }
}

// Export singleton instance
export const variantIntegrationService = VariantIntegrationService.getInstance()
export default VariantIntegrationService